import { useParams, Navigate, Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Calendar, AlertTriangle } from "lucide-react";

import "../../styles/marketing.css";
import Seo, { breadcrumbJsonLd, faqJsonLd, SITE_URL } from "../../components/marketing/Seo";
import MarketingNav from "../../components/marketing/MarketingNav";
import MarketingFooter from "../../components/marketing/MarketingFooter";
import Breadcrumbs from "../../components/marketing/Breadcrumbs";
import CtaBanner from "../../components/marketing/CtaBanner";
import Reveal from "../../components/marketing/Reveal";
import { BrowserFrame } from "../../components/marketing/Frames";
import { getIndustry } from "../../data/industriesData";
import { getFeature } from "../../data/featuresData";

export default function IndustryPage() {
  const { slug } = useParams();
  const industry = getIndustry(slug);
  if (!industry) return <Navigate to="/industries" replace />;

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Industries", path: "/industries" },
    { name: industry.name, path: `/industries/${industry.slug}` },
  ];
  const related = industry.related.map(getIndustry).filter(Boolean);

  return (
    <div className="mk min-h-screen overflow-x-hidden" data-testid={`industry-page-${industry.slug}`}>
      <Seo
        title={industry.seoTitle}
        description={industry.seoDescription}
        path={`/industries/${industry.slug}`}
        image={`${SITE_URL}/marketing/og/og-industries.jpg`}
        jsonLd={[breadcrumbJsonLd(crumbs), faqJsonLd(industry.faqs)]}
      />
      <MarketingNav />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-14 md:pb-20 bg-gradient-to-b from-blue-50/70 via-white to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <Breadcrumbs items={crumbs} />
          </div>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[var(--mk-blue)] mb-4 flex items-center gap-2">
                <industry.icon className="w-4 h-4" /> {industry.name} Software
              </p>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-5" data-testid="industry-h1">
                {industry.h1}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">{industry.intro}</p>
              <div className="flex flex-col sm:flex-row gap-3.5 mb-5">
                <Link to="/register" className="mk-btn mk-btn-primary mk-btn-lg" data-testid="industry-trial-btn">
                  Start Free for 7 Days
                </Link>
                <Link to="/book-demo" className="mk-btn mk-btn-outline mk-btn-lg" data-testid="industry-demo-btn">
                  <Calendar className="w-4 h-4" /> Book a Demo
                </Link>
              </div>
              <p className="flex items-center gap-1.5 text-sm text-slate-500 font-medium">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" /> No credit card required
              </p>
            </div>
            <div className="rounded-3xl bg-gradient-to-tr from-blue-50/60 via-white to-indigo-50/40 p-3 sm:p-5 border border-slate-200/60">
              <BrowserFrame src={industry.img} alt={industry.imgAlt} eager />
            </div>
          </div>
        </div>
      </section>

      {/* Pain points */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-[1.2]">Sound familiar?</h2>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-5">
            {industry.painPoints.map((p, i) => (
              <Reveal key={p.title} delay={Math.min(i + 1, 3)}>
                <div className="h-full rounded-2xl border border-amber-200/70 bg-amber-50/40 p-6">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mb-3" />
                  <h3 className="text-[16px] font-semibold text-slate-900 mb-2">{p.title}</h3>
                  <p className="text-[14px] text-slate-600 leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-16 md:py-20 bg-[var(--mk-bg-2)] border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-[1.2] mb-3">
              How Fieldz AI fixes it for {industry.name.toLowerCase()} businesses
            </h2>
            <p className="text-lg text-slate-600">Four capabilities that do the heaviest lifting in your trade.</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-5">
            {industry.solutions.map((s, i) => {
              const feat = getFeature(s.featureSlug);
              return (
                <Reveal key={s.title} delay={Math.min((i % 2) + 1, 2)}>
                  <Link
                    to={`/features/${s.featureSlug}`}
                    className="group block h-full rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_16px_32px_-12px_rgba(15,23,42,0.14)] hover:border-blue-200"
                    data-testid={`industry-solution-${s.featureSlug}`}
                  >
                    {feat && (
                      <div className="w-11 h-11 rounded-xl bg-[var(--mk-blue-light)] flex items-center justify-center mb-4">
                        <feat.icon className="w-5 h-5 text-[var(--mk-blue)]" />
                      </div>
                    )}
                    <h3 className="text-[17px] font-semibold text-slate-900 mb-2">{s.title}</h3>
                    <p className="text-[14.5px] text-slate-600 leading-relaxed mb-3">{s.body}</p>
                    <span className="inline-flex items-center gap-1 text-[14px] font-semibold text-[var(--mk-blue)]">
                      About {feat ? feat.name.toLowerCase() : "this feature"}
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 text-center mb-10">
            {industry.name} software — frequently asked questions
          </h2>
          <div className="space-y-4">
            {industry.faqs.map((f, i) => (
              <div key={f.q} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`industry-faq-${i}`}>
                <h3 className="text-slate-900 font-semibold mb-2 text-[15.5px]">{f.q}</h3>
                <p className="text-slate-600 text-[14.5px] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related industries */}
      <section className="py-12 bg-[var(--mk-bg-2)] border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-6">Also built for</h2>
          <div className="flex flex-wrap gap-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/industries/${r.slug}`}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 bg-white text-[14px] font-semibold text-slate-700 transition-all hover:border-blue-300 hover:text-[var(--mk-blue)] hover:-translate-y-0.5"
                data-testid={`related-industry-${r.slug}`}
              >
                <r.icon className="w-4 h-4 text-[var(--mk-blue)]" /> {r.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner
        title={`Run your ${industry.name.toLowerCase()} business from one platform`}
        body="Scheduling, phone, invoicing, and payments — set up in minutes with a free 7-day trial."
      />
      <MarketingFooter />
    </div>
  );
}
